// @flow

import _ from 'lodash';
import moment from 'moment';
import Dispatcher from 'dispatcher';
import ApiClient from 'utils/ApiClient';
import * as HomeActionTypes from 'action_types/HomeActionTypes';
import Home from 'models/Home';
import Asset from 'models/homes/Asset';
import HomeOnBoarding from 'models/HomeOnBoarding';
import HomeMeetingOwner from 'models/HomeMeetingOwner';
import CancelableRequests from 'utils/CancelableRequests';

class HomeActions {
  cancelableRequests = new CancelableRequests();

  get_homes(params: {}, success?: () => void): Promise<any> {
    const filteredParams = _.pickBy(params, v => v !== null && v !== undefined);
    return ApiClient.get({
      ...this.cancelableRequests.cancelableParams,
      path: '/homes.json',
      data: filteredParams,
      success: response => {
        if (success !== undefined) {
          success();
        }
        Dispatcher.dispatch({
          type: HomeActionTypes.LIST_HOMES,
          homes: response.homes.map(home => new Home(home)),
          pageSelector: 0,
          metadata: {
            min_price: response.meta.min_price,
            max_price: response.meta.max_price,
            min_sqare_mts: response.meta.min_sqare_mts,
            max_sqare_mts: response.meta.max_sqare_mts,
            apartment_type: response.meta.apartment_type
          }
        });
      }
    });
  }

  getHome(
    slug: string = '',
    callBackError: any = () => {},
    isCancelable: boolean = false
  ): Promise<any> {
    const cancelableRequests = () =>
      isCancelable ? this.cancelableRequests.cancelableParams : {};

    return ApiClient.get({
      ...cancelableRequests(),
      path: `/homes/${slug}/show_public.json`,
      skipRedirect: true,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_HOME,
          home: new Home(response.home)
        });
      },
      error: error => {
        if (error.status === 404) {
          callBackError();
        }
      }
    });
  }

  getHomeById(id: string = ''): Promise<any> {
    return ApiClient.get({
      ...this.cancelableRequests.cancelableParams,
      path: `/homes/${id}.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_HOME_BY_ID,
          home: new Home(response.home)
        });
      }
    });
  }

  getSimilarHomes(id: string = ''): Promise<any> {
    return ApiClient.get({
      path: `/homes/${id}/similar_homes.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.LIST_SIMILAR_HOMES,
          homes: response.homes.map(home => new Home(home))
        });
      }
    });
  }

  getPriceLimits(): Promise<any> {
    return ApiClient.get({
      path: `/homes/price_limits.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_PRICE_LIMITS,
          price_limits: response
        });
      }
    });
  }

  getMostRecentHomes(): Promise<any> {
    return ApiClient.get({
      ...this.cancelableRequests.cancelableParams,
      path: `/homes/most_recents.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.LIST_MOST_RECENT_HOMES,
          homes: response.homes.map(home => new Home(home))
        });
      }
    });
  }

  createHomeOB(params: any): Promise<any> {
    return ApiClient.post({
      path: `/homes.json`,
      jsonData: params,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.CREATE_HOME_ONBOARDING,
          home: new HomeOnBoarding(response.home),
          metadata: response.meta
        });
      }
    });
  }

  getHomeOB(id: string = ''): Promise<any> {
    return ApiClient.get({
      path: `/homes/${id}.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.EDIT_HOME_ONBOARDING,
          home: new HomeOnBoarding(response.home),
          metadata: response.meta
        });
      }
    });
  }

  getHomeByIdWithOnBoarding(id: string = ''): Promise<any> {
    return ApiClient.get({
      path: `/homes/${id}.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.EDIT_HOME_ONBOARDING,
          home: new HomeOnBoarding(response.home),
          metadata: response.meta
        });
      }
    });
  }

  updateHomeOB(
    home_id: string,
    data: any,
    success?: () => void,
    ignoreResponse?: boolean = false
  ): Promise<any> {
    return ApiClient.patch({
      path: `/homes/${home_id}.json`,
      jsonData: data,
      success: response => {
        if (!ignoreResponse) {
          Dispatcher.dispatch({
            type: HomeActionTypes.UPDATE_HOME_ONBOARDING,
            home: new HomeOnBoarding(response.home),
            metadata: response.meta
          });
        }
        if (success !== undefined) {
          success();
        }
      }
    });
  }

  deleteHomeOB(
    user_id: string,
    home_id: string,
    success?: () => void
  ): Promise<any> {
    return ApiClient.delete({
      path: `/users/${user_id}/homes/${home_id}.json`,
      success: response => {
        if (success !== undefined) {
          success();
        }
      }
    });
  }

  uploadBanner(
    home_id: string,
    asset_type: string,
    file: File,
    success?: () => void
  ): Promise<any> {
    let formData = new FormData();
    formData.append('file', file);
    formData.append('is_temporary_banner', true);
    return ApiClient.uploadFile(
      `/api/internal/homes/${home_id}/assets/${asset_type}.json`,
      formData,
      response_string => {
        if (success !== undefined) {
          success(file);
        }
      },
      'POST'
    );
  }

  uploadAsset(
    home_id: string,
    asset_type: string,
    file: File,
    temp_asset_id: string
  ): Promise<any> {
    let formData = new FormData();
    formData.append('file', file);
    return ApiClient.uploadFile(
      `/api/internal/homes/${home_id}/assets/${asset_type}.json`,
      formData,
      response_string => {
        const response = JSON.parse(response_string);
        Dispatcher.dispatch({
          type: HomeActionTypes.UPLOAD_HOME_ASSET,
          asset: new Asset(
            response.asset.id,
            response.asset.image_file_name,
            response.asset.image_url,
            asset_type,
            null
          ),
          temp_asset_id: temp_asset_id
        });
      },
      'POST'
    );
  }

  addTempAsset(asset: Asset): Promise<any> {
    return new Promise((resolve, reject) => {
      Dispatcher.dispatch({
        type: HomeActionTypes.ADD_TEMP_HOME_ASSET,
        asset: asset
      });
      resolve();
    });
  }

  deleteAsset(
    home_id: string,
    asset_id: string,
    asset_type: string
  ): Promise<any> {
    return ApiClient.delete({
      path: `/homes/${home_id}/assets/${asset_id}.json`,
      data: { asset_type: asset_type },
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.DELETE_HOME_ASSET,
          asset_id: asset_id,
          asset_type: asset_type
        });
      }
    });
  }

  getListHomeAssets(
    home_id: string,
    asset_type: string,
    haveChecking: boolean = false
  ): Promise<any> {
    return ApiClient.get({
      path: `/homes/${home_id}/assets/${asset_type}.json`,
      success: async response => {
        let accepted = [];
        let declined = [];
        if (haveChecking) {
          accepted = await this.getPhotoTypeAssets(
            home_id,
            asset_type,
            'accepted'
          );
          declined = await this.getPhotoTypeAssets(
            home_id,
            asset_type,
            'declined'
          );
        }
        Dispatcher.dispatch({
          type: HomeActionTypes.LIST_HOME_ASSETS,
          asset_type,
          assets: response.assets.map(
            asset =>
              new Asset(
                asset.id,
                asset.image_file_name,
                asset.image_url,
                asset_type,
                null
              )
          ),
          accepted: accepted.map(
            asset =>
              new Asset(
                asset.id,
                asset.image_file_name,
                asset.image_url,
                asset_type,
                null
              )
          ),
          declined: declined.map(
            asset =>
              new Asset(
                asset.id,
                asset.image_file_name,
                asset.image_url,
                asset_type,
                null,
                asset.checking_status && asset.checking_status.explanation
                  ? asset.checking_status.explanation
                  : ''
              )
          )
        });
      }
    });
  }

  getPhotoTypeAssets(home_id: string, asset_type: string, photos_type: string) {
    return new Promise(resolve => {
      return ApiClient.get({
        path: `/homes/${home_id}/assets/${asset_type}?photos_type=${photos_type}`,
        success: response => {
          resolve(response.assets);
        }
      });
    });
  }

  createSimilarHomeOB(
    home_id: string,
    success?: () => void,
    isFromAgency?: boolean
  ): Promise<any> {
    return ApiClient.post({
      path: `/homes.json`,
      jsonData: {
        role: isFromAgency ? 'advisor' : 'owner',
        home_id: home_id
      },
      success: response => {
        success();
        Dispatcher.dispatch({
          type: HomeActionTypes.CREATE_SIMILAR_HOME_ONBOARDING,
          home: new HomeOnBoarding(response.home),
          metadata: response.meta
        });
      }
    });
  }

  getHomesByUser(user_id: string = ''): Promise<any> {
    return ApiClient.get({
      ...this.cancelableRequests.cancelableParams,
      path: `/users/${user_id}/homes.json`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_HOMES_BY_USER,
          homes: response.homes.map(home => new Home(home)),
          metadata: response.meta
        });
      }
    });
  }

  getHomeMeetingsByStatus(
    userId: string,
    homeId: string,
    filterType: string,
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.get({
      path: `/users/${userId}/homes/${homeId}/home_meetings.json`,
      data: { filter_type: filterType },
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_HOME_HOME_MEETINGS,
          home_meetings: response.home_meetings.map(
            home_meeting => new HomeMeetingOwner(home_meeting)
          ),
          cancellationReasons: response.meta
        });
      },
      error: error
    });
  }

  getHomeHomeMeetings(
    home_id,
    data = {},
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.get({
      path: `/homes/${home_id}/home_meetings.json`,
      data: data,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_HOME_HOME_MEETINGS,
          home_meetings: response.home_meetings.map(
            home_meeting => new HomeMeetingOwner(home_meeting)
          ),
          cancellationReasons: response.meta
        });
      },
      error: error
    });
  }

  createHomeAppointment(
    home_id,
    data,
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.post({
      path: `/homes/${home_id}/home_meetings.json`,
      data: data,
      success: success,
      error: response => {
        error && error(response);
      }
    });
  }

  setDepaPause(
    user_id: string,
    home_id: string,
    data: any,
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.put({
      path: `/users/${user_id}/homes/${home_id}/unpublish.json`,
      jsonData: data,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.SET_HOME_PAUSE,
          home_id
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error
    });
  }

  setDepaPublished(
    user_id: string,
    home_id: string,
    success?: () => void,
    error?: () => void
  ): Promise<any> {
    return ApiClient.put({
      path: `/users/${user_id}/homes/${home_id}`,
      jsonData: { id: home_id },
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.SET_HOME_PUBLISHED,
          home_id
        });
        if (success !== undefined) {
          success();
        }
      },
      error: error
    });
  }

  deleteHome(
    home_id: string,
    user_id: string,
    success?: () => void,
    error?: () => void
  ) {
    return ApiClient.delete({
      path: `/users/${user_id}/homes/${home_id}`,
      success: response => {
        Dispatcher.dispatch({
          type: HomeActionTypes.DELETE_HOME,
          home_id
        });
        if (success !== undefined) {
          success();
        }
      },
      error: () => {
        if (error !== undefined) {
          error();
        }
      }
    });
  }

  getBelongPosts(homeId: string) {
    const authorName = article => {
      return (
        article.author_name || `${article.user.name} ${article.user.last_name}`
      );
    };

    return ApiClient.get({
      path: `/homes/${homeId}/articles.json`,
      success: ({ articles }) => {
        const formattedPosts = articles.map(article => ({
          id: article.id,
          title: article.title,
          author: authorName(article),
          date: moment(article.published_at).format('DD [de] MMMM [de] YYYY'),
          banner: article.pictures[0],
          slug: article.slug
        }));
        Dispatcher.dispatch({
          type: HomeActionTypes.LIST_BELONG_POSTS,
          posts: formattedPosts.slice(0, 2)
        });
      }
    });
  }

  cancelAllRequests() {
    this.cancelableRequests.cancelAllRequests();
  }

  recommendedPrice(data: {}) {
    return ApiClient.post({
      data,
      path: '/recommended_prices',
      success: ({ recommended_price_feature }) => {
        Dispatcher.dispatch({
          type: HomeActionTypes.GET_RECOMMENDED_PRICE,
          recommended_price_feature
        });
      }
    });
  }
}

export default new HomeActions();
